// 網站地圖的拓樸圖（框架無關：佈局＋繪製＋互動都在這裡，Vue 只包一層 → src/components/pages/pageSitemap.vue）。
//
// 移植自 muse-bot memory-viz-v3/src/modules/monolab/InfraGraph.tsx @ cd5c0c225（讀取時 muse-bot HEAD 368e1f2b7）
// 的 InfraCanvas。照搬的部分：
//   ① live simulation：d3-force 分層 forceX（layerX＝欄）、forceY 置中、forceManyBody、forceCollide、
//      分邊型 forceLink（contains 短而強＝骨架、depends 中距、resolves 長而極弱＝註記、hosts 只對齊 y），
//      alphaDecay .015／velocityDecay .35，先同步預跑 300 tick 再 fit，之後 alphaTarget .004 微溫常駐（會飄）；
//   ② zoom／pan：滾輪以游標為中心縮放、空白處拖曳平移、雙擊全覽；layoutRect() 除回 CSS zoom；
//      fit 之前整個圖層隱形（opacity 綁「fit 已發生」），免得先畫一版再跳；
//   ③ 拖曳節點＝升溫（alphaTarget .25），放手回微溫；
//   ④ hover／聚焦節點 ⇒ 鄰邊高亮＋關係字、其餘退場（節點 .14、邊 .07）；篩選類型 ⇒ 其他類型退場；
//   ⑤ 節點樣式：圓底色＝類型（infra 是專案）、單色線條圖示＝形狀（ServiceIcon 的作法，圖形換成全站搜尋的
//      src/search/kinds.mjs 類型圖示；索引頁用 ServiceIcon 的「服務級 hub」圖形）、根節點用品牌標（infra 的 provider logo）、
//      黑描邊字標（paintOrder stroke）、葉節點平常不標字，放大到 1.4× 或聚焦才標（infra 服務層的規則）、
//      脈動外環（infra 的風險環，這裡標搜尋符合）。
// 改了的部分（為什麼）：
//   - React 的 JSX 改成直接建 SVG DOM（框架無關）；tick 本來就是 DOM 直改，原樣保留；
//   - 節點數會從 32 長到 978（草圖 783 頁）：展開／收合時保留既有節點位置、新節點從父節點長出來；
//     預跑分批放進 requestAnimationFrame（每幀 ≤10ms），不鎖住主執行緒；超過 300 個節點不常駐微溫（省電）；
//   - 依賴邊的箭頭縮到目標圓邊（infra 固定 refX，大泡泡會把箭頭蓋住）；
//   - 加了觸控：雙指縮放、第一下點選預覽、再點才前往；加了鍵盤：方向鍵平移、＋／－縮放、0 全覽、Enter 開啟；
//   - prefers-reduced-motion：不常駐微溫、不畫脈動、全覽不做鏡頭滑行。
import { forceCollide, forceLink, forceManyBody, forceSimulation, forceX, forceY } from 'd3-force'
import { KIND_ICON } from '../search/kinds.mjs'
import { KIND_COLOR, titleFor } from './sitemap-model.mjs'
import { SYMBOL_NOREG } from './brand-marks.mjs'

const NS = 'http://www.w3.org/2000/svg'
// ServiceIcon 的「服務級 hub」圖形（16×16 座標）：一個中心點三向散開＝一頁收著很多頁
const HUB_ICON_16 = '<circle cx="8" cy="8" r="1.6"/><circle cx="8" cy="3" r="1.3"/><circle cx="3.7" cy="11.5" r="1.3"/><circle cx="12.3" cy="11.5" r="1.3"/><path d="M8 6.4V4.3M6.9 9.1l-2.1 1.6M9.1 9.1l2.1 1.6"/>'
// 根節點的品牌標：WU 符號內聯成向量（2026-10-04 起；原本是 38 KB 的白色 PNG `<image>`）
const ROOT_LOGO = SYMBOL_NOREG
const SIMMER = 0.004
const SIMMER_MAX_NODES = 300
const TICKS = 300
let instances = 0

const el = (tag, attrs = {}) => {
  const node = document.createElementNS(NS, tag)
  for (const [k, v] of Object.entries(attrs)) if (v != null) node.setAttribute(k, String(v))
  return node
}
// 字寬（中日韓算 2）截斷：圖上字標最長 28 個半形寬
const wide = ch => /[ᄀ-ᅟ⺀-〾ぁ-㏿㐀-䶿一-鿿가-힣豈-﫿︰-﹯＀-｠￠-￦]/.test(ch)
export function clip(text, max = 28) {
  let w = 0, out = ''
  for (const ch of String(text)) { w += wide(ch) ? 2 : 1; if (w > max) return out.trimEnd() + '…'; out += ch }
  return out
}

/**
 * host：position:relative 的容器（圖會塞一個 <svg> 進去）。
 * opts.onActivate(node, { newTab })：點擊／Enter；opts.onHover(id|null)；opts.onSelect(id|null)（觸控第一下＝預覽）
 */
// mulberry32：小型確定性亂數，只在預渲染用
function seededRandom(seed) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function createSitemapGraph(host, opts = {}) {
  const locale = opts.locale === 'zh' ? 'zh' : 'en'
  const labels = opts.labels || {}
  const reduced = !!opts.reducedMotion
  const onActivate = opts.onActivate || (() => {})
  const onHover = opts.onHover || (() => {})
  const onSelect = opts.onSelect || (() => {})
  const uid = 'smg' + (++instances)

  const svg = el('svg', { class: 'smg-svg', width: '100%', height: '100%', tabindex: '0', role: 'group', 'aria-label': labels.graph || 'Site map' })
  const defs = el('defs')
  defs.innerHTML =
    `<marker id="${uid}-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M 0 1 L 9 5 L 0 9 z" class="smg-arrow"/></marker>` +
    `<marker id="${uid}-arrow-hot" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path d="M 0 1 L 9 5 L 0 9 z" class="smg-arrow-hot"/></marker>`
  const viewG = el('g', { class: 'smg-view' })
  const edgeLayer = el('g', { class: 'smg-edges' })
  const edgeLabelLayer = el('g', { class: 'smg-edge-labels', 'aria-hidden': 'true' })
  const nodeLayer = el('g', { class: 'smg-nodes' })
  viewG.append(edgeLayer, edgeLabelLayer, nodeLayer)
  svg.append(defs, viewG)
  host.prepend(svg)

  // ── 狀態 ──
  let dims = measure()
  let view = { x: 0, y: 0, k: 1 }, fitted = false
  let sim = null, simNodes = [], simEdges = [], byId = new Map(), maxLayer = 1, maxCount = 1
  let focusId = null, selectedId = null, filterKind = null, matches = null, expanded = new Set()
  let neighbors = new Set(), runId = 0, raf = 0, glide = 0, visible = true
  const nodeEls = new Map(), edgeEls = new Map(), edgeLabelEls = new Map()
  let minK = 0.3

  function measure() {
    return { w: host.clientWidth || 1100, h: Math.max(320, svg.clientHeight || host.clientHeight || 560) }
  }
  // 2026-08-22 infra 的教訓：CSS zoom 下 getBoundingClientRect 回的是視覺座標，一律除回佈局座標
  function layoutRect() {
    const r = svg.getBoundingClientRect()
    const z = parseFloat(getComputedStyle(document.documentElement).zoom) || 1
    return { left: r.left, top: r.top, width: r.width / z, height: r.height / z, z }
  }
  function applyView() {
    viewG.setAttribute('transform', `translate(${view.x},${view.y}) scale(${view.k})`)
    svg.classList.toggle('k-big', view.k >= 1.4)
    if (fitted) viewG.classList.add('is-ready')
  }

  // ── 尺寸與樣式 ──
  const isLeaf = n => n.type === 'page' && !!n.group
  const nodeR = n => {
    if (n.type === 'page' && n.key === '/') return 22
    if (n.type === 'group') return 11 + 15 * Math.sqrt((n.count || 1) / maxCount)
    if (isLeaf(n)) return 5
    if (n.hub) return 14
    return n.layer >= 2 ? 8 : 10
  }
  const colorOf = n => KIND_COLOR[n.kind] || KIND_COLOR.page
  const labelOf = n => {
    if (n.type === 'page' && n.key === '/') return 'cheyuwu.com'
    // 索引頁、單頁的字標只留冒號前的主名（「履歷：展覽、獎項與教學」→「履歷」），全名在資訊卡與清單；葉節點留全名
    const base = n.type === 'group' ? `${titleFor(n, locale)} ×${n.count}`
      : isLeaf(n) ? clip(titleFor(n, locale), 22) : clip(titleFor(n, locale).split(/：|: /)[0], 24)
    const hits = n.type === 'group' && matches ? matchCount(n) : 0
    return hits ? `${base} · ${labels.matches ? labels.matches(hits) : hits}` : base
  }
  function matchCount(g) {
    let n = 0
    const members = g.members || []
    for (const id of members) if (matches.has(id)) n++
    return n
  }
  const isMatch = n => !!matches && (n.type === 'page' ? matches.has(n.id) : !expanded.has(n.id) && matchCount(n) > 0)
  const iconMarkup = (n, size) => {
    const box = n.hub && n.type === 'page' ? 16 : 24
    const inner = n.hub && n.type === 'page' ? HUB_ICON_16 : (KIND_ICON[n.kind] || KIND_ICON.page)
    const s = size / box
    return `<g transform="translate(${-size / 2},${-size / 2}) scale(${s})" class="smg-icon" stroke-width="${(box === 16 ? 1.35 : 1.9).toFixed(2)}">${inner}</g>`
  }
  const ariaOf = n => {
    const zh = locale === 'zh'
    if (n.type === 'group') return `${titleFor(n, locale)}${zh ? '，' : ', '}${n.count}${labels.pagesUnit || ' pages'}${expanded.has(n.id) ? labels.expandedSuffix || '' : labels.collapsedSuffix || ''}`
    const url = n.urls[locale] || n.urls.en || n.urls.zh
    return zh ? `${titleFor(n, locale)}（${url}）` : `${titleFor(n, locale)} (${url})`
  }

  function paintNode(g, n) {
    const r = nodeR(n), leaf = isLeaf(n)
    g.textContent = ''
    g.setAttribute('class', ['smg-node', 't-' + n.type, leaf ? 'is-leaf' : '', n.hub ? 'is-hub' : '', n.noindex ? 'is-noindex' : '',
      n.type === 'page' && !(n.urls.en && n.urls.zh) ? 'is-single' : '', expanded.has(n.id) ? 'is-open' : '', isMatch(n) ? 'is-match' : ''].filter(Boolean).join(' '))
    g.setAttribute('aria-label', ariaOf(n))
    if (n.type === 'group') g.setAttribute('aria-expanded', String(expanded.has(n.id)))
    if (isMatch(n)) {
      const ring = el('circle', { r: r + 5, class: 'smg-ring' })
      if (!reduced) ring.innerHTML = '<animate attributeName="opacity" values="0.9;0.3;0.9" dur="2.2s" repeatCount="indefinite"/>'
      g.append(ring)
    }
    if (n.type === 'group' && expanded.has(n.id)) g.append(el('circle', { r: r + 3.5, class: 'smg-open-ring' }))
    const dot = el('circle', { r, class: 'smg-dot' })
    if (!(n.type === 'page' && n.key === '/')) dot.style.fill = colorOf(n)
    g.append(dot)
    if (n.type === 'page' && n.key === '/') {
      const logo = el('svg', { viewBox: ROOT_LOGO.viewBox, x: -r * 0.42, y: -r * 0.42, width: r * 0.84, height: r * 0.84, class: 'smg-logo', 'aria-hidden': 'true' })
      logo.append(el('path', { d: ROOT_LOGO.wu, fill: '#f4f4f2' }))
      g.append(logo)
    } else if (!leaf) {
      const holder = el('g')
      holder.innerHTML = iconMarkup(n, r > 15 ? 22 : r > 9 ? 15 : 11)
      g.append(holder.firstChild)
    }
    const main = !leaf && n.layer <= 2
    const text = leaf
      ? el('text', { x: r + 4, y: 3, class: 'smg-label is-minor' })
      : el('text', { y: r + 12, 'text-anchor': 'middle', class: 'smg-label' + (main ? '' : ' is-minor') })
    text.textContent = labelOf(n)
    g.append(text)
  }

  function makeNode(n) {
    const g = el('g', { tabindex: '0', role: n.type === 'group' ? 'button' : 'link', 'data-id': n.id })
    g.addEventListener('pointerdown', ev => onNodePointerDown(ev, n.id))
    // 滑過時把節點釘住：微溫會讓小節點飄走，游標底下的東西不該自己跑掉
    g.addEventListener('pointerenter', ev => {
      if (ev.pointerType === 'touch') return
      const d = simNodes.find(x => x.id === n.id)
      if (d && !drag) { d.fx = d.x; d.fy = d.y; d.hoverPin = true }
      setFocus(n.id, true)
    })
    g.addEventListener('pointerleave', ev => {
      if (ev.pointerType === 'touch') return
      const d = simNodes.find(x => x.id === n.id)
      if (d?.hoverPin && drag?.id !== n.id) { d.fx = null; d.fy = null; d.hoverPin = false }
      if (focusId === n.id) setFocus(selectedId, true)
    })
    g.addEventListener('focus', () => { setFocus(n.id, true); ensureVisible(n.id) })
    g.addEventListener('blur', () => { if (focusId === n.id) setFocus(selectedId, true) })
    g.addEventListener('keydown', ev => {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); activate(n.id, { newTab: ev.metaKey || ev.ctrlKey }) }
    })
    g.addEventListener('auxclick', ev => { if (ev.button === 1) { ev.preventDefault(); activate(n.id, { newTab: true }) } })
    return g
  }

  // ── 邊 ──
  const edgeKey = e => `${e.source}>${e.target}`
  const idOf = end => (typeof end === 'object' ? end.id : end)
  function makeEdge(e) {
    const line = el('line', { class: `smg-edge t-${e.type}${e.member ? ' is-member' : ''}` })
    if (e.type === 'lists') line.setAttribute('marker-end', `url(#${uid}-arrow)`)
    return line
  }

  // ── 重建模擬（infra：dims／節點數變了就重建；這裡另外保留舊位置） ──
  function setGraph(graph, { refit = true, expandedIds, fitIds } = {}) {
    if (expandedIds) expanded = new Set(expandedIds)
    const prev = new Map(simNodes.map(d => [d.id, d]))
    const nodes = graph.nodes
    byId = new Map(nodes.map(n => [n.id, n]))
    maxLayer = Math.max(1, ...nodes.map(n => n.layer))
    maxCount = Math.max(1, ...nodes.filter(n => n.type === 'group' && !n.parentGroup).map(n => n.count))
    minK = nodes.length > 60 ? 0.12 : 0.3
    const { w, h } = dims
    const padX = Math.min(110, w * 0.1)
    const layerX = layer => padX + (w - 2 * padX) * (layer / maxLayer)
    // 第一欄有二十頁：沒有下層的單頁（履歷、聯絡、Muse…）往首頁那側退半欄，索引頁留在原欄，
    // 一欄拆成兩欄，字標才不會疊成一團（infra 每欄最多十來個節點，沒有這個問題）
    const colOf = n => (n.type === 'page' && !n.hub && n.layer === 1 ? 0.5 : n.layer)
    const parentOf = new Map()
    for (const e of graph.edges) if (e.type === 'contains' || !parentOf.has(e.target)) parentOf.set(e.target, e.source)
    const fresh = !simNodes.length

    // 預渲染（無頭瀏覽器）用固定種子，同一份資料每次排出同一張圖；真人瀏覽器維持 Math.random。
    const rand = typeof navigator !== 'undefined' && navigator.webdriver === true ? seededRandom(20261004) : Math.random
    simNodes = nodes.map(n => {
      const old = prev.get(n.id)
      if (old) return { ...old, fx: null, fy: null, n }
      const p = prev.get(parentOf.get(n.id))
      return p
        ? { id: n.id, n, x: p.x + (rand() - 0.5) * 30, y: p.y + (rand() - 0.5) * 30 }
        : { id: n.id, n, x: layerX(colOf(n)) + (rand() - 0.5) * 60, y: h / 2 + (rand() - 0.5) * h * 0.7 }
    })
    const simById = new Map(simNodes.map(d => [d.id, d]))
    simEdges = graph.edges.filter(e => simById.has(e.source) && simById.has(e.target)).map(e => ({ ...e }))

    // DOM：鍵值 diff（留著的節點不重建，避免焦點跳掉）
    const keepEdges = new Set(simEdges.map(edgeKey))
    for (const [k, line] of edgeEls) if (!keepEdges.has(k)) { line.remove(); edgeEls.delete(k) }
    for (const e of simEdges) { const k = edgeKey(e); if (!edgeEls.has(k)) { const line = makeEdge(e); edgeEls.set(k, line); edgeLayer.append(line) } }
    for (const [id, g] of nodeEls) if (!byId.has(id)) { g.remove(); nodeEls.delete(id) }
    // 既有節點不搬動（搬動 DOM 會讓鍵盤焦點掉）；新節點插在 nodes 順序的前一個後面 ⇒ 群組展開後，Tab 下一站就是它的成員
    let prevEl = null
    for (const n of nodes) {
      let g = nodeEls.get(n.id)
      if (!g) {
        g = makeNode(n); nodeEls.set(n.id, g)
        nodeLayer.insertBefore(g, prevEl ? prevEl.nextSibling : nodeLayer.firstChild)
      }
      prevEl = g
      paintNode(g, n)
    }
    for (const lab of edgeLabelEls.values()) lab.remove()
    edgeLabelEls.clear()
    if (focusId && !byId.has(focusId)) focusId = null
    if (selectedId && !byId.has(selectedId)) selectedId = null

    // 分邊型力學（infra 2026-08-15）：contains＝結構骨架（短而強）；lists（infra depends）＝中距；
    // also（infra resolves）＝跨索引的註記，長而極弱，不把一群拖離自己的家；
    // 群→成員（infra hosts）：距離＝兩欄的 x 距離 ⇒ 幾乎只對齊 y，不把頁拖出自己的欄。
    sim?.stop()
    const leaf = d => isLeaf(d.n)
    const linkDist = l => (l.member ? Math.max(40, Math.abs(layerX(colOf(l.source.n)) - layerX(colOf(l.target.n))))
      : l.type === 'contains' ? 55 : l.type === 'lists' ? 90 : 150)
    const linkStrength = l => (l.member ? 0.08 : l.type === 'contains' ? 0.55 : l.type === 'lists' ? 0.18 : 0.02)
    // 手機直立也維持左到右（試過直立改上到下：第一欄 20 頁擠成一橫排，比左到右更窄、字更小，2026-10-03 退回）
    sim = forceSimulation(simNodes)
      .force('x', forceX(d => layerX(colOf(d.n))).strength(d => (leaf(d) ? 0.3 : 0.55)))
      .force('y', forceY(h / 2).strength(0.05))
      .force('charge', forceManyBody().strength(d => (leaf(d) ? -28 : -160)))
      .force('collide', forceCollide(d => nodeR(d.n) + (leaf(d) ? 4 : d.n.layer <= 2 ? 20 : 10)))
      .force('link', forceLink(simEdges).id(d => d.id).distance(linkDist).strength(linkStrength))
      .alphaDecay(0.015)
      .velocityDecay(0.35)
      .on('tick', paint)
      .stop()
    updateClasses()
    // 先預跑收斂再 fit（infra：初期 charge 暴力期 fit 會算到爆炸的 bounding box）。
    // 小圖同步跑完；大圖分批進 rAF，每幀 ≤10ms，邊跑邊畫（新長出來的節點看得到從父節點散開）。
    sim.alpha(fresh ? 0.9 : 0.7)
    const id = ++runId
    const finish = () => {
      if (id !== runId) return
      paint()
      if (refit || !fitted) fit({ animate: fitted, ids: fitIds && fitIds.size ? fitIds : undefined })
      const prerendering = typeof navigator !== 'undefined' && navigator.webdriver === true  // 預渲染：不微溫，快照才每次相同
      const simmer = !reduced && !prerendering && simNodes.length <= SIMMER_MAX_NODES
      sim.alphaTarget(simmer ? SIMMER : 0)
      if (simmer && visible) sim.restart()
    }
    if (simNodes.length <= 150) { for (let i = 0; i < TICKS; i++) sim.tick(); finish(); return }
    let done = 0
    const step = () => {
      if (id !== runId) return
      const t0 = performance.now()
      while (done < TICKS && performance.now() - t0 < 10) { sim.tick(); done++ }
      paint()
      if (done < TICKS) raf = requestAnimationFrame(step); else finish()
    }
    if (!fitted) { for (let i = 0; i < 40; i++, done++) sim.tick() }
    raf = requestAnimationFrame(step)
  }

  function paint() {
    for (const d of simNodes) nodeEls.get(d.id)?.setAttribute('transform', `translate(${d.x.toFixed(1)},${d.y.toFixed(1)})`)
    for (const e of simEdges) {
      const line = edgeEls.get(edgeKey({ source: idOf(e.source), target: idOf(e.target) }))
      const s = e.source, t = e.target
      if (!line || typeof s !== 'object') continue
      let x2 = t.x, y2 = t.y
      if (e.type === 'lists') { // 箭頭停在目標圓邊（infra 固定 refX，大泡泡會把箭頭吃掉）
        const dx = t.x - s.x, dy = t.y - s.y, len = Math.hypot(dx, dy) || 1, cut = nodeR(t.n) + 3
        x2 = t.x - (dx / len) * cut; y2 = t.y - (dy / len) * cut
      }
      line.setAttribute('x1', s.x.toFixed(1)); line.setAttribute('y1', s.y.toFixed(1))
      line.setAttribute('x2', x2.toFixed(1)); line.setAttribute('y2', y2.toFixed(1))
      const lab = edgeLabelEls.get(line)
      if (lab) { lab.setAttribute('x', ((s.x + t.x) / 2).toFixed(1)); lab.setAttribute('y', ((s.y + t.y) / 2 - 4).toFixed(1)) }
    }
  }

  // ── 鄰接高亮、篩選、搜尋符合（React 版是每次 render 重算 opacity；這裡改 class） ──
  function updateClasses() {
    neighbors = new Set()
    const hot = new Set()
    if (focusId) for (const e of simEdges) {
      const s = idOf(e.source), t = idOf(e.target)
      if (s === focusId) { neighbors.add(t); hot.add(e) }
      if (t === focusId) { neighbors.add(s); hot.add(e) }
    }
    const dimmed = id => {
      const n = byId.get(id)
      return !!filterKind && !!n && !(n.type === 'page' && n.key === '/') && n.kind !== filterKind
    }
    for (const [id, g] of nodeEls) {
      g.classList.toggle('is-focus', id === focusId)
      g.classList.toggle('is-selected', id === selectedId)
      g.classList.toggle('is-nb', neighbors.has(id))
      g.classList.toggle('is-faded', !!focusId && id !== focusId && !neighbors.has(id))
      g.classList.toggle('is-dim', dimmed(id))
    }
    for (const lab of edgeLabelEls.values()) lab.remove()
    edgeLabelEls.clear()
    for (const e of simEdges) {
      const s = idOf(e.source), t = idOf(e.target)
      const line = edgeEls.get(`${s}>${t}`)
      if (!line) continue
      const isNb = hot.has(e)
      line.classList.toggle('is-nb', isNb)
      line.classList.toggle('is-faded', !!focusId && !isNb)
      line.classList.toggle('is-dim', dimmed(s) || dimmed(t))
      if (e.type === 'lists') line.setAttribute('marker-end', `url(#${uid}-${isNb ? 'arrow-hot' : 'arrow'})`)
      // 關係字：只在鄰接高亮時出現（infra：常駐會變蚊香）；群→成員的線太多，不標字
      if (isNb && !e.member && labels.edge) {
        const text = el('text', { class: 'smg-edge-label', 'text-anchor': 'middle' })
        text.textContent = labels.edge[e.type] || e.type
        edgeLabelLayer.append(text)
        edgeLabelEls.set(line, text)
      }
    }
    paint()
  }
  function setFocus(id, fromGraph = false) {
    if (focusId === id) return
    focusId = id && byId.has(id) ? id : null
    updateClasses()
    if (fromGraph) onHover(focusId)
  }

  // ── fit view（雙擊／按鈕／初始都走這裡；ids＝只框這些節點，搜尋用） ──
  function fit({ ids, animate = true } = {}) {
    const pick = ids ? simNodes.filter(d => ids.has(d.id)) : simNodes
    if (!pick.length) return
    const rect = layoutRect()
    if (!rect.width || !rect.height) return
    const xs = pick.map(d => d.x), ys = pick.map(d => d.y)
    const labelRoom = pick.some(d => isLeaf(d.n)) ? 130 : 60   // 葉節點字標在右側，右邊多留字寬（infra 服務層同理）
    const minX = Math.min(...xs) - 60, maxX = Math.max(...xs) + labelRoom
    const minY = Math.min(...ys) - 50, maxY = Math.max(...ys) + 50
    // 疊在圖上的圖例條、縮放鈕佔掉的邊（infra 的圖例疊在左下，節點會被蓋住；這裡 fit 時讓開）
    const inset = { top: 0, right: 0, bottom: 0, left: 0, ...(opts.insets?.() || {}) }
    const W = Math.max(80, rect.width - inset.left - inset.right), H = Math.max(80, rect.height - inset.top - inset.bottom)
    const k = Math.min(2.2, Math.max(minK, Math.min(W / (maxX - minX), H / (maxY - minY))))
    const target = { k, x: inset.left + (W - (minX + maxX) * k) / 2, y: inset.top + (H - (minY + maxY) * k) / 2 }
    const first = !fitted
    fitted = true
    if (first || !animate || reduced) { cancelAnimationFrame(glide); view = target; applyView(); return }
    // 鏡頭滑行 280ms（全站 --dur-base、減速停下不回彈）
    const from = { ...view }, t0 = performance.now(), dur = 280
    const ease = t => 1 - Math.pow(1 - t, 3)
    cancelAnimationFrame(glide)
    const go = now => {
      const t = Math.min(1, (now - t0) / dur), e = ease(t)
      view = { k: from.k + (target.k - from.k) * e, x: from.x + (target.x - from.x) * e, y: from.y + (target.y - from.y) * e }
      applyView()
      if (t < 1) glide = requestAnimationFrame(go)
    }
    glide = requestAnimationFrame(go)
  }
  // 鍵盤 Tab 到框外的節點：鏡頭平移把它帶進框（滑鼠點到的一定在框內，不會觸發）
  function ensureVisible(id) {
    const d = simNodes.find(x => x.id === id)
    const r = layoutRect()
    if (!d || !r.width) return
    const sx = view.x + d.x * view.k, sy = view.y + d.y * view.k, m = 40
    if (sx > m && sx < r.width - m && sy > m && sy < r.height - m) return
    cancelAnimationFrame(glide)
    view = { ...view, x: r.width / 2 - d.x * view.k, y: r.height / 2 - d.y * view.k }
    applyView()
  }
  function zoomAt(factor, cx, cy) {
    cancelAnimationFrame(glide)
    const k2 = Math.min(4, Math.max(minK, view.k * factor))
    if (cx == null) { const r = layoutRect(); cx = r.width / 2; cy = r.height / 2 }
    view = { k: k2, x: cx - ((cx - view.x) * k2) / view.k, y: cy - ((cy - view.y) * k2) / view.k }
    applyView()
  }

  // ── 滾輪縮放（non-passive，以游標為中心） ──
  const onWheel = e => {
    e.preventDefault()
    const r = layoutRect()
    zoomAt(e.deltaY < 0 ? 1.12 : 0.89, (e.clientX - r.left) / r.z, (e.clientY - r.top) / r.z)
  }
  svg.addEventListener('wheel', onWheel, { passive: false })

  // ── 平移（空白處拖曳）、雙指縮放、節點拖曳（升溫） ──
  let drag = null
  const pointers = new Map()
  const toSim = (clientX, clientY) => {
    const r = layoutRect()
    return { x: ((clientX - r.left) / r.z - view.x) / view.k, y: ((clientY - r.top) / r.z - view.y) / view.k }
  }
  const onSvgPointerDown = ev => {
    if (ev.button !== 0 && ev.pointerType === 'mouse') return
    pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY })
    svg.setPointerCapture?.(ev.pointerId)
    cancelAnimationFrame(glide)
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()]
      if (drag?.mode === 'node') releaseNode()
      drag = { mode: 'pinch', dist: Math.hypot(a.x - b.x, a.y - b.y), view: { ...view }, mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } }
      return
    }
    if (!drag) drag = { mode: 'pan', sx: ev.clientX, sy: ev.clientY, vx: view.x, vy: view.y, moved: 0 }
  }
  function onNodePointerDown(ev, id) {
    if (ev.button !== 0 && ev.pointerType === 'mouse') return
    ev.stopPropagation()
    pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY })
    // 捕捉在節點上（infra 同作法）：捕捉在 svg 會讓節點立刻收到 pointerleave，高亮閃一下
    ev.currentTarget.setPointerCapture?.(ev.pointerId)
    cancelAnimationFrame(glide)
    if (pointers.size === 2) { onSvgPointerDown(ev); return }
    const d = simNodes.find(x => x.id === id)
    drag = { mode: 'node', id, sx: ev.clientX, sy: ev.clientY, moved: 0, pointerType: ev.pointerType, heated: false, d }
  }
  const onPointerMove = ev => {
    if (pointers.has(ev.pointerId)) pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY })
    if (!drag) return
    if (drag.mode === 'pinch') {
      if (pointers.size < 2) return
      const [a, b] = [...pointers.values()]
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1
      const r = layoutRect()
      const k2 = Math.min(4, Math.max(minK, drag.view.k * (dist / drag.dist)))
      const mx = (drag.mid.x - r.left) / r.z, my = (drag.mid.y - r.top) / r.z
      const nx = ((a.x + b.x) / 2 - r.left) / r.z, ny = ((a.y + b.y) / 2 - r.top) / r.z
      view = { k: k2, x: nx - ((mx - drag.view.x) * k2) / drag.view.k, y: ny - ((my - drag.view.y) * k2) / drag.view.k }
      applyView()
      return
    }
    drag.moved = Math.max(drag.moved, Math.hypot(ev.clientX - drag.sx, ev.clientY - drag.sy))
    if (drag.mode === 'pan') {
      view = { ...view, x: drag.vx + (ev.clientX - drag.sx), y: drag.vy + (ev.clientY - drag.sy) }
      applyView()
      svg.classList.add('is-panning')
    } else if (drag.mode === 'node' && drag.d && drag.moved > 4) {
      if (!drag.heated) { drag.heated = true; drag.d.fx = drag.d.x; drag.d.fy = drag.d.y; sim?.alphaTarget(0.25).restart() } // 拖曳升溫，周圍跟著讓位
      const p = toSim(ev.clientX, ev.clientY)
      drag.d.fx = p.x; drag.d.fy = p.y
    }
  }
  function releaseNode() {
    if (drag?.mode !== 'node' || !drag.heated || !drag.d) return
    drag.d.fx = null; drag.d.fy = null; drag.d.hoverPin = false
    const simmer = !reduced && simNodes.length <= SIMMER_MAX_NODES
    sim?.alphaTarget(simmer ? SIMMER : 0) // 放手回微溫
  }
  const onPointerUp = ev => {
    pointers.delete(ev.pointerId)
    const d = drag
    if (!d) return
    if (d.mode === 'pinch') { if (pointers.size === 0) drag = null; return }
    drag = null
    svg.classList.remove('is-panning')
    if (d.mode === 'node') {
      releaseNode()
      if (d.moved <= 4 && ev.type === 'pointerup') {
        // 觸控：第一下＝預覽（資訊卡），同一顆再點一下才前往；滑鼠：hover 已經預覽過，點下去直接前往
        const n = byId.get(d.id)
        if (d.pointerType === 'touch' && n?.type === 'page' && selectedId !== d.id) select(d.id)
        else activate(d.id, { newTab: ev.metaKey || ev.ctrlKey || ev.shiftKey })
      }
    } else if (d.mode === 'pan' && d.moved <= 4 && ev.type === 'pointerup') select(null)
  }
  svg.addEventListener('pointerdown', onSvgPointerDown)
  svg.addEventListener('pointermove', onPointerMove)
  svg.addEventListener('pointerup', onPointerUp)
  svg.addEventListener('pointercancel', onPointerUp)
  svg.addEventListener('dblclick', e => { if (!e.target.closest?.('.smg-node')) fit() })

  // ── 鍵盤：方向鍵平移、＋／－縮放、0 全覽、Esc 取消選取 ──
  svg.addEventListener('keydown', ev => {
    const step = 60
    const k = ev.key
    if (k === 'ArrowLeft' || k === 'ArrowRight' || k === 'ArrowUp' || k === 'ArrowDown') {
      ev.preventDefault()
      view = { ...view, x: view.x + (k === 'ArrowLeft' ? step : k === 'ArrowRight' ? -step : 0), y: view.y + (k === 'ArrowUp' ? step : k === 'ArrowDown' ? -step : 0) }
      applyView()
    } else if (k === '+' || k === '=') { ev.preventDefault(); zoomAt(1.25) }
    else if (k === '-' || k === '_') { ev.preventDefault(); zoomAt(0.8) }
    else if (k === '0') { ev.preventDefault(); fit() }
    else if (k === 'Escape') { select(null); setFocus(null, true) }
  })

  function select(id) {
    selectedId = id && byId.has(id) ? id : null
    if (selectedId) setFocus(selectedId, true)
    else updateClasses()
    onSelect(selectedId)
  }
  function activate(id, how = {}) {
    const n = byId.get(id)
    if (n) onActivate(n, how)
  }

  // ── 尺寸：寬度變了重建佈局（欄位置跟著寬度），只有高度變只重新 fit（手機網址列伸縮不該讓圖重排） ──
  let lastGraph = null
  const ro = new ResizeObserver(() => {
    const next = measure()
    const widthChanged = Math.abs(next.w - dims.w) > 2
    dims = next
    if (widthChanged && lastGraph) setGraph(lastGraph, { refit: true })
    else if (simNodes.length) fit({ animate: false })
  })
  ro.observe(host)
  // 看不到時不跑微溫（省電）：離開視窗或分頁切走就停，回來再開
  const io = typeof IntersectionObserver === 'function' ? new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting && document.visibilityState !== 'hidden'
    if (!sim) return
    if (visible && sim.alphaTarget() > 0) sim.restart(); else if (!visible) sim.stop()
  }) : null
  io?.observe(host)
  const onVisibility = () => {
    visible = document.visibilityState !== 'hidden'
    if (sim && visible && sim.alphaTarget() > 0) sim.restart(); else if (sim && !visible) sim.stop()
  }
  document.addEventListener('visibilitychange', onVisibility)

  return {
    setGraph(graph, options) { lastGraph = graph; setGraph(graph, options) },
    setFilter(kind) { filterKind = kind || null; updateClasses() },
    setMatches(set) {
      matches = set && set.size ? set : set ? new Set() : null
      for (const [id, g] of nodeEls) paintNode(g, byId.get(id))
      updateClasses()
    },
    focus(id) { setFocus(id || selectedId) },
    select,
    fit,
    zoomIn() { zoomAt(1.25) },
    zoomOut() { zoomAt(0.8) },
    nodeIds() { return simNodes.map(d => d.id) },
    destroy() {
      runId++
      cancelAnimationFrame(raf); cancelAnimationFrame(glide)
      sim?.stop()
      ro.disconnect(); io?.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      svg.removeEventListener('wheel', onWheel)
      svg.remove()
    },
  }
}
